import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { lcCoachingApi } from '@/api/lcCoaching'
import { getApiErrorMessage } from '@/utils/apiError'
import { coachingKeys } from '@/hooks/coaching/useCoachingToday'
import type {
  CoachingLessonDetailResponse,
  ComponentProgressDetailView,
  LearnerTodayResponse,
  LessonProcessDetailView,
  OverrideQuizAnswerRequest,
} from '@/types/coaching'
import { gradingDraftScope, useClearGradingDraftRow } from './useGradingDraft'
import { reviewNoteSourceKeys } from './useReviewNote'

interface OverrideQuizAnswerOptions {
  /**
   * false면 성공/실패 토스트를 띄우지 않는다(채점표가 여러 문항을 순차 저장한 뒤 결과를 1회만 알릴 때).
   * 캐시 낙관 패치·롤백·재조정은 동일하게 수행한다. 기본 true.
   */
  notify?: boolean
  /** false면 채점표의 전체 저장 완료 시 호출자가 library를 한 번 갱신한다. 기본 true. */
  refreshLibrary?: boolean
}

/**
 * 교사 정오답 수정 mutation (C2).
 * - onMutate: 대상 문제의 isCorrect를 낙관적으로 즉시 패치(칩 색 즉시 반영) + 롤백 스냅샷 보관.
 * - onSuccess: 서버가 돌려준 component detail로 해당 컴포넌트 전체를 교체(권위 있는 재조정).
 * - onError: 스냅샷 롤백 + 에러 토스트.
 */
export function useOverrideQuizAnswer(learnerId: number, options: OverrideQuizAnswerOptions = {}) {
  const { notify = true, refreshLibrary = true } = options
  const qc = useQueryClient()
  const { t } = useTranslation('coaching')
  const clearDraftRow = useClearGradingDraftRow()
  const key = coachingKeys.learnerToday(learnerId)

  return useMutation({
    mutationFn: (body: OverrideQuizAnswerRequest) =>
      lcCoachingApi.overrideQuizAnswer(learnerId, body),
    onMutate: async (body) => {
      const detailQuery = {
        queryKey: coachingKeys.lessonDetailPath(learnerId, body.lessonId),
      }
      await Promise.all([
        qc.cancelQueries({ queryKey: key }),
        qc.cancelQueries(detailQuery),
      ])
      const prev = qc.getQueryData<LearnerTodayResponse>(key)
      const prevDetails = qc.getQueriesData<CoachingLessonDetailResponse>(detailQuery)
      if (prev) qc.setQueryData<LearnerTodayResponse>(key, patchQuestion(prev, body))
      qc.setQueriesData<CoachingLessonDetailResponse>(detailQuery, (cur) =>
        cur ? patchDetailQuestion(cur, body) : cur,
      )
      return { prev, prevDetails }
    },
    onError: (err, _body, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev) // 롤백
      ctx?.prevDetails.forEach(([detailKey, data]) => {
        if (data !== undefined) qc.setQueryData(detailKey, data)
      })
      if (notify) toast.error(getApiErrorMessage(err, t))
    },
    onSuccess: (updated: ComponentProgressDetailView, body) => {
      if (body.scope === 'pause' && body.answers != null) {
        clearDraftRow(gradingDraftScope.pause(updated.componentProgressId), body.quizId)
      }
      qc.setQueryData<LearnerTodayResponse>(key, (cur) =>
        cur ? spliceComponent(cur, updated) : cur,
      )
      qc.setQueriesData<CoachingLessonDetailResponse>(
        { queryKey: coachingKeys.lessonDetailPath(learnerId, body.lessonId) },
        (cur) => cur ? spliceDetailComponent(cur, updated) : cur,
      )
      void qc.invalidateQueries({ queryKey: reviewNoteSourceKeys.previews(learnerId) })
      if (refreshLibrary) {
        void qc.invalidateQueries({ queryKey: coachingKeys.library(learnerId, 'all') })
      }
      if (notify) toast.success(t('override.saved'))
    },
  })
}

// ── 순수 immutable 헬퍼 ──────────────────────────────────────────

/**
 * 낙관적 패치: 대상 컴포넌트(configComponentId)에서 scope별로 대상 문제의 isCorrect만 교체.
 * - scope === 'session': quiz.questions 의 quizId 일치 문제.
 * - scope === 'pause': video.pauseQuizResponses 의 quizId(+pauseTimeMs) 일치 응답.
 * 매칭 행이 없으면(미응시 pause 등) 해당 컴포넌트는 그대로 두고 onSuccess가 재조정한다.
 * 최소/부분 shape(테스트 픽스처)도 통과하도록 방어적으로 접근한다.
 */
function patchQuestion(
  data: LearnerTodayResponse,
  body: OverrideQuizAnswerRequest,
): LearnerTodayResponse {
  // 제출값 수정(answers)은 재채점 결과(isCorrect)를 FE가 미리 모름 → 낙관 패치 생략, onSuccess(spliceComponent) 재조정에 맡긴다.
  if (body.answers != null) return data
  const lessons = data.lessons
  if (!lessons) return data
  return {
    ...data,
    lessons: lessons.map((lesson) => patchLesson(lesson, body)),
  }
}

function patchLesson(
  lesson: LessonProcessDetailView,
  body: OverrideQuizAnswerRequest,
): LessonProcessDetailView {
  const components = lesson.components
  if (!components) return lesson
  let changed = false
  const next = components.map((component) => {
    if (component.configComponentId !== body.configComponentId) return component
    const patched = patchComponent(component, body)
    if (patched !== component) changed = true
    return patched
  })
  if (!changed) return lesson
  return { ...lesson, components: next }
}

function patchDetailQuestion(
  data: CoachingLessonDetailResponse,
  body: OverrideQuizAnswerRequest,
): CoachingLessonDetailResponse {
  if (body.answers != null) return data
  return { ...data, initialAttempt: patchLesson(data.initialAttempt, body) }
}

function patchComponent(
  component: ComponentProgressDetailView,
  body: OverrideQuizAnswerRequest,
): ComponentProgressDetailView {
  if (body.scope === 'pause') {
    const responses = component.video?.pauseQuizResponses
    if (!responses) return component
    let changed = false
    const nextResponses = responses.map((r) => {
      const match =
        r.quizId === body.quizId &&
        (body.pauseTimeMs == null || r.pauseTimeMs === body.pauseTimeMs)
      if (!match || r.isCorrect === body.isCorrect) return r
      changed = true
      return { ...r, isCorrect: body.isCorrect }
    })
    if (!changed) return component
    return {
      ...component,
      video: { ...component.video!, pauseQuizResponses: nextResponses },
    }
  }

  // scope === 'session'
  const questions = component.quiz?.questions
  if (!questions) return component
  let changed = false
  const nextQuestions = questions.map((q) => {
    if (q.quizId !== body.quizId || q.isCorrect === body.isCorrect) return q
    changed = true
    return { ...q, isCorrect: body.isCorrect }
  })
  if (!changed) return component
  return {
    ...component,
    quiz: { ...component.quiz!, questions: nextQuestions },
  }
}

/**
 * 권위 있는 재조정: configComponentId가 일치하는 컴포넌트를 서버 응답 detail로 통째 교체.
 */
function spliceComponent(
  data: LearnerTodayResponse,
  comp: ComponentProgressDetailView,
): LearnerTodayResponse {
  const lessons = data.lessons
  if (!lessons) return data
  return {
    ...data,
    lessons: lessons.map((lesson) => {
      const components = lesson.components
      if (!components) return lesson
      let changed = false
      const next = components.map((component) => {
        if (component.configComponentId !== comp.configComponentId) return component
        changed = true
        return comp
      })
      if (!changed) return lesson
      return { ...lesson, components: next }
    }),
  }
}

function spliceDetailComponent(
  data: CoachingLessonDetailResponse,
  comp: ComponentProgressDetailView,
): CoachingLessonDetailResponse {
  const lesson = data.initialAttempt
  const components = lesson.components
  if (!components) return data
  let changed = false
  const next = components.map((component) => {
    if (component.configComponentId !== comp.configComponentId) return component
    changed = true
    return comp
  })
  if (!changed) return data
  return { ...data, initialAttempt: { ...lesson, components: next } }
}
